import React from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { ThumbsUp, ThumbsDown } from "lucide-react-native";
import {
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useDiseaseHistory } from "../../DiseaseHistoryContext";
import type { DiseaseHistoryStackParamList } from "../../DiseaseHistoryNavigator";
import { useFlowProgress } from "../../useFlowProgress";

export function CancerHistoryScreen() {
  const t = useTranslations();
  const { state, updateState, save } = useDiseaseHistory();
  const navigation =
    useNavigation<NativeStackNavigationProp<DiseaseHistoryStackParamList>>();
  const progress = useFlowProgress("CancerHistory");

  const selection: "yes" | "no" | null =
    state.hasCancerHistory === null
      ? null
      : state.hasCancerHistory
        ? "yes"
        : "no";

  const handleSelection = (value: boolean) => {
    if (value) {
      updateState({ hasCancerHistory: true });
    } else {
      // Delete existing cancers when selecting "no"
      const idsToDelete = state.cancers.filter((c) => c.id).map((c) => c.id!);
      updateState({
        hasCancerHistory: false,
        cancers: [],
        cancerIdsToDelete: [...state.cancerIdsToDelete, ...idsToDelete],
      });
    }
  };

  const handleSave = async () => {
    await save();
    const currentIdx = state.navigationStack.indexOf("CancerHistory");
    const nextRoute = state.navigationStack[currentIdx + 1];
    if (nextRoute) {
      navigation.navigate(nextRoute);
    }
  };

  return (
    <QuestionLayout
      title={t("med_history.medical.disease_history.cancer_history.title")}
      subtitle={t(
        "med_history.medical.disease_history.cancer_history.subtitle",
      )}
      onBack={() => navigation.goBack()}
      progress={progress}
      onSave={handleSave}
      saveDisabled={selection === null}
      saveLabel={t("med_history.medical.shared.next")}
    >
      <View style={styles.optionsContainer}>
        <SquareOptionButton
          icon={ThumbsUp}
          label={t("med_history.shared.yes")}
          isSelected={selection === "yes"}
          onPress={() => handleSelection(true)}
          iconSize={24}
          containerSize={48}
          labelSize="lg"
        />
        <SquareOptionButton
          icon={ThumbsDown}
          label={t("med_history.shared.no")}
          isSelected={selection === "no"}
          onPress={() => handleSelection(false)}
          iconSize={24}
          containerSize={48}
          labelSize="lg"
        />
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  optionsContainer: {
    flexDirection: "row",
    gap: 12,
    marginBottom: 40,
    width: "100%",
  },
});
